import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Rocket, FileSignature, Calendar, Loader2, Link as LinkIcon, Users, Settings, Play, Pause, Archive, BarChart3, CheckCircle2, AlertCircle, Layers, Edit2, UserPlus, Search, RefreshCw } from 'lucide-react';
import api from '../api/api';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import DataTable from './ui/DataTable';
import { formatDate, formatDateTime, normalizeApiError } from '../lib/i18n';
import { TimezoneSelector } from './ui/TimezoneSelector';
import { getCampaignRecipientTimelineDate } from './campaignRecipientUtils';

const getCampaignRecipientTriggerLabel = (recipient, t) => {
  if (recipient.status === 'sent') return t('campaignDetail.recipient.deliveryCompleted');
  if (recipient.status === 'error') return recipient.currentAction || recipient.waitReason || t('campaignDetail.recipient.deliveryFailed');
  if (recipient.status === 'processing') return recipient.currentAction || t('campaignDetail.recipient.processing');
  if (recipient.waitReason) return recipient.waitReason;
  if (recipient.sendAt) return t('campaignDetail.recipient.waitingForWindow');
  return t('campaignDetail.recipient.queuedForEvaluation');
};

const getCampaignRecipientNextAction = (recipient, t, i18n) => {
  if (recipient.status === 'sent') {
    const senderEmail = recipient.assignedMailAccount?.user || recipient.assignedMailAccount?.from;
    const sentDate = recipient.sentAt || recipient.updatedAt;

    if (senderEmail) {
      return t('campaignDetail.recipient.sentVia', {
        email: senderEmail,
        date: sentDate ? formatDateTime(sentDate, {}, i18n.language) : '',
      });
    }

    return recipient.providerMessageId
      ? t('campaignDetail.recipient.providerAccepted', {
          id: recipient.providerMessageId,
        })
      : t('campaignDetail.recipient.awaitingEngagement');
  }
  if (recipient.status === 'error') {
    return recipient.logs?.length
      ? recipient.logs[recipient.logs.length - 1]
      : t('campaignDetail.recipient.inspectLogs');
  }
  if (recipient.sendAt) {
    return t('campaignDetail.recipient.nextAttempt', {
      date: formatDateTime(recipient.sendAt, {}, i18n.language),
    });
  }
  if (recipient.status === 'processing') {
    return t('campaignDetail.recipient.preparingChecks');
  }
  return t('campaignDetail.recipient.sendWhenAllowed');
};

const getCampaignRecipientSourceLabel = (recipient, t) => {
  const sourceLabels = {
    campaign_expansion: t('campaignDetail.recipient.audienceExpansion'),
    crm_campaign_binding: t('campaignDetail.recipient.directCrmAdd'),
    apollo: t('campaignDetail.recipient.apolloImport')
  };
  return sourceLabels[recipient.source] || recipient.source?.replace(/_/g, ' ') || t('campaignDetail.recipient.campaignQueue');
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);
  const [isLaunchConfirmOpen, setIsLaunchConfirmOpen] = useState(false);
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);

  // Recipients Table State
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recPage, setRecPage] = useState(1);
  const [recLimit, setRecLimit] = useState(10);
  const [recTotalRecords, setRecTotalRecords] = useState(0);
  const [recSearch, setRecSearch] = useState('');
  const [recStatusFilter, setRecStatusFilter] = useState('All');
  const [recSortBy, setRecSortBy] = useState('sendAt');
  const [recSortOrder, setRecSortOrder] = useState('asc');
  const [refreshKey, setRefreshKey] = useState(0);

  // Audience editing
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [isAudienceEditorOpen, setIsAudienceEditorOpen] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [savingAudience, setSavingAudience] = useState(false);
  const [syncingAudience, setSyncingAudience] = useState(false);
  const [syncingQueues, setSyncingQueues] = useState(false);

  // Contact add flow
  const [isAddContactsOpen, setIsAddContactsOpen] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [newAudienceGroupName, setNewAudienceGroupName] = useState('');
  const [addingContacts, setAddingContacts] = useState(false);

  // Sender editing
  const [isSenderEditorOpen, setIsSenderEditorOpen] = useState(false);
  const [senderEditMode, setSenderEditMode] = useState('ACCOUNT');
  const [availableMailAccounts, setAvailableMailAccounts] = useState([]);
  const [availableSenderPools, setAvailableSenderPools] = useState([]);
  const [selectedMailAccountId, setSelectedMailAccountId] = useState('');
  const [selectedSenderPoolId, setSelectedSenderPoolId] = useState('');
  const [newPoolName, setNewPoolName] = useState('');
  const [newPoolAccountIds, setNewPoolAccountIds] = useState([]);
  const [newPoolRotationStrategy, setNewPoolRotationStrategy] = useState('ROUND_ROBIN');
  const [savingSender, setSavingSender] = useState(false);

  // Delivery rules editing
  const [isRulesEditorOpen, setIsRulesEditorOpen] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [startMode, setStartMode] = useState('IMMEDIATE');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [sendingWindowStart, setSendingWindowStart] = useState('09:00');
  const [sendingWindowEnd, setSendingWindowEnd] = useState('17:30');
  const [minDelaySec, setMinDelaySec] = useState(60);
  const [maxDelaySec, setMaxDelaySec] = useState(180);
  const [perDayLimit, setPerDayLimit] = useState(0);
  const [dispatchSortPolicy, setDispatchSortPolicy] = useState('COMPANY_SEQUENTIAL');
  const [openTracking, setOpenTracking] = useState(true);
  const [clickTracking, setClickTracking] = useState(true);

  const handleRetryRecipient = async (recipientId) => {
    try {
      await api.post(`/api/campaigns/${id}/recipients/${recipientId}/retry`);
      toast.success(t('campaignDetail.messages.recipientQueuedForRetry'));
      setRefreshKey(prev => prev + 1);
      fetchMetrics();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.retryFailed'));
    }
  };

  const handleRetryAllFailed = async () => {
    const ok = window.confirm(t('campaignDetail.messages.retryAllFailedConfirm', { defaultValue: 'Hata alan tüm alıcıları tekrar sıraya sokmak istediğinize emin misiniz?' }));
    if (!ok) return;

    try {
      const res = await api.post(`/api/campaigns/${id}/retry-all-failed`);
      toast.success(res.data.message || t('campaignDetail.messages.bulkRetrySuccess'));
      setRefreshKey(prev => prev + 1);
      fetchMetrics();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.bulkRetryFailed'));
    }
  };

  const handleSendRecipientNow = async (recipientId) => {
    const ok = window.confirm(t('campaignDetail.messages.sendRecipientNowConfirm', { defaultValue: 'Bu e-postayı şimdi göndermek istediğinizden emin misiniz?' }));
    if (!ok) return;

    try {
      await api.post(`/api/campaigns/${id}/recipients/${recipientId}/send-now`);
      toast.success(t('campaignDetail.messages.recipientSentNow', { defaultValue: 'E-posta hemen gönderildi!' }));
      setRefreshKey(prev => prev + 1);
      fetchMetrics();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.sendNowFailed', { defaultValue: 'Hemen gönderme işlemi başarısız oldu.' }));
    }
  };

  const handleRemoveRecipient = async (recipientId) => {
    const ok = window.confirm(t('campaignDetail.messages.removeRecipientConfirm'));
    if (!ok) return;

    try {
      await api.delete(`/api/campaigns/${id}/recipients/${recipientId}`);
      toast.success(t('campaignDetail.messages.recipientRemoved'));
      setRefreshKey(prev => prev + 1);
      fetchMetrics();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.removeRecipientFailed'));
    }
  };

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await api.get(`/api/campaigns/${id}/execution-summary`);
      setMetrics(res.data.data);
    } catch (err) {
      console.error("Failed to load metrics", err);
    }
  }, [id]);

  const fetchCampaign = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/campaigns/${id}`);
      setCampaign(res.data);
    } catch (err) {
      setError(normalizeApiError(err, t, 'campaignDetail.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const fetchDetailedAnalytics = useCallback(async () => {
    try {
      const res = await api.get(`/api/analytics/campaign/${id}`);
      setDetailedAnalytics(res.data.data);
    } catch (err) {
      console.error("Failed to load detailed analytics", err);
    }
  }, [id]);

  useEffect(() => {
    fetchCampaign();
    fetchDetailedAnalytics();
  }, [fetchCampaign, fetchDetailedAnalytics]);

  useEffect(() => {
    if (campaign && (campaign.status === 'RUNNING' || campaign.status === 'QUEUED' || campaign.status === 'PAUSED')) {
      fetchMetrics();
      // Poll metrics every 10 seconds if running
      let interval;
      if (campaign.status === 'RUNNING' || campaign.status === 'QUEUED') {
        interval = setInterval(() => {
            fetchMetrics();
            setRefreshKey(prev => prev + 1);
        }, 10000);
      }
      return () => { if (interval) clearInterval(interval); };
    }
  }, [campaign, fetchMetrics]); // eslint-disable-line no-use-before-define

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await api.patch(`/api/campaigns/${id}/status`, { status: newStatus });
      toast.success(res.data.message || t('campaignDetail.messages.statusChanged', { status: newStatus }));
      fetchCampaign();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.changeStatusFailed'));
    }
  };

  const handleLaunch = () => setIsLaunchConfirmOpen(true);

  const confirmLaunch = async (options = {}) => {
    console.log("Launching with options:", options);
    setIsLaunchConfirmOpen(false);
    setLaunching(true);
    setLaunchResult(null);
    try {
      const res = await api.post(`/api/campaigns/${id}/launch`, options);
      setLaunchResult(res.data.data); // The metrics object from service
      setCampaign(prev => ({ ...prev, status: 'QUEUED' }));
      fetchMetrics();
      toast.success(t('campaignDetail.messages.launchSuccess'));
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.launchFailed'));
    } finally {
      setLaunching(false);
    }
  };


  const fetchAvailableGroups = async () => {
    setGroupsLoading(true);
    try {
      const res = await api.get('/api/groups', { params: { limit: 1000, sortBy: 'createdAt', sortOrder: 'desc' } });
      setAvailableGroups(res.data?.data || res.data || []);
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.loadGroupsFailed'));
    } finally {
      setGroupsLoading(false);
    }
  };

  const fetchSenderDependencies = async () => {
    try {
      const [mailRes, poolRes] = await Promise.all([
        api.get('/api/mail-accounts'),
        api.get('/api/sender-pools')
      ]);
      setAvailableMailAccounts(mailRes.data?.accounts || []);
      setAvailableSenderPools(poolRes.data?.data || []);
    } catch {
      toast.error(t('campaignDetail.messages.loadSenderOptionsFailed'));
    }
  };

  const fetchAvailableContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const res = await api.get('/api/contacts', {
        params: { limit: 60, search: contactSearch }
      });
      setAvailableContacts(res.data?.data || []);
    } catch {
      toast.error(t('campaignDetail.messages.loadContactsFailed'));
    } finally {
      setContactsLoading(false);
    }
  }, [contactSearch, t]);

  const openAudienceEditor = () => {
    setSelectedGroupIds(campaign.groupIds?.map(g => g._id || g) || []);
    setIsAudienceEditorOpen(true);
    fetchAvailableGroups();
  };

  const toggleSelectedGroup = (groupId) => {
    setSelectedGroupIds(prev => (
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    ));
  };

  const handleSaveAudience = async () => {
    setSavingAudience(true);
    try {
      await api.put(`/api/campaigns/${id}`, { groupIds: selectedGroupIds });
      await fetchCampaign();
      setIsAudienceEditorOpen(false);
      toast.success(t('campaignDetail.messages.audienceUpdated'));
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.audienceUpdateFailed'));
    } finally {
      setSavingAudience(false);
    }
  };

  const handleSyncAudience = async () => {
    setSyncingAudience(true);
    try {
      const res = await api.post(`/api/campaigns/${id}/expand`);
      const { totalQueued, totalSkipped } = res.data.data;
      toast.success(t('campaignDetail.messages.syncSuccess', { 
        queued: totalQueued,
        skipped: totalSkipped,
        defaultValue: `Audience synced! ${totalQueued} contacts added, ${totalSkipped} skipped.` 
      }));
      setRefreshKey(prev => prev + 1);
      fetchMetrics();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.syncFailed', { 
        defaultValue: 'Failed to sync audience.' 
      }));
    } finally {
      setSyncingAudience(false);
    }
  };

  const handleSyncQueues = async () => {
    setSyncingQueues(true);
    try {
      await api.post(`/api/campaigns/${id}/reschedule`);
      toast.success(t('campaignDetail.messages.syncQueuesSuccess', { 
        defaultValue: 'Queues rescheduled to current time!' 
      }));
      setRefreshKey(prev => prev + 1);
      fetchMetrics();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.syncQueuesFailed', { 
        defaultValue: 'Failed to reschedule queues.' 
      }));
    } finally {
      setSyncingQueues(false);
    }
  };

  const openAddContactsModal = () => {
    const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
    setSelectedContactIds(new Set());
    setContactSearch('');
    setNewAudienceGroupName(`${campaign.name} Audience ${stamp}`);
    setIsAddContactsOpen(true);
    fetchAvailableContacts();
  };

  const openSenderEditor = async () => {
    await fetchSenderDependencies();

    if (campaign.senderPoolId?._id || campaign.senderPoolId) {
      setSenderEditMode('POOL');
      setSelectedSenderPoolId(campaign.senderPoolId?._id || campaign.senderPoolId || '');
      setSelectedMailAccountId('');
    } else {
      setSenderEditMode('ACCOUNT');
      setSelectedMailAccountId(campaign.mailAccountId?._id || campaign.mailAccountId || '');
      setSelectedSenderPoolId('');
    }

    setNewPoolName(`${campaign.name} Sender Pool`);
    setNewPoolAccountIds(campaign.senderPoolId?.mailAccountIds?.map(acc => acc._id || acc) || []);
    setNewPoolRotationStrategy(campaign.senderPoolId?.rotationStrategy || 'ROUND_ROBIN');
    setIsSenderEditorOpen(true);
  };

  const toggleNewPoolAccount = (accountId) => {
    setNewPoolAccountIds(prev => (
      prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
    ));
  };

  const handleSaveSender = async () => {
    setSavingSender(true);
    try {
      let payload = {};

      if (senderEditMode === 'ACCOUNT') {
        if (!selectedMailAccountId) {
          toast.warning(t('campaignDetail.messages.selectSenderAccount'));
          return;
        }
        payload = { mailAccountId: selectedMailAccountId, senderPoolId: null };
      } else if (senderEditMode === 'POOL') {
        if (!selectedSenderPoolId) {
          toast.warning(t('campaignDetail.messages.selectSenderPool'));
          return;
        }
        payload = { senderPoolId: selectedSenderPoolId, mailAccountId: null };
      } else {
        if (!newPoolName.trim()) {
          toast.warning(t('campaignDetail.messages.poolNameRequired'));
          return;
        }
        if (newPoolAccountIds.length === 0) {
          toast.warning(t('campaignDetail.messages.selectPoolAccount'));
          return;
        }

        const createdPool = await api.post('/api/sender-pools', {
          name: newPoolName.trim(),
          mailAccountIds: newPoolAccountIds,
          rotationStrategy: newPoolRotationStrategy,
          isActive: true
        });

        const createdPoolId = createdPool.data?.data?._id;
        payload = { senderPoolId: createdPoolId, mailAccountId: null };
      }

      await api.put(`/api/campaigns/${id}`, payload);
      await fetchCampaign();
      setIsSenderEditorOpen(false);
      toast.success(t('campaignDetail.messages.sendingIdentityUpdated'));
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.sendingIdentityUpdateFailed'));
    } finally {
      setSavingSender(false);
    }
  };

  const toggleContact = (contactId) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  const handleAddContacts = async () => {
    if (selectedContactIds.size === 0) return;

    const selectedIds = Array.from(selectedContactIds);
    const shouldCreateGroup = ['DRAFT', 'ARCHIVED', 'SCHEDULED'].includes(campaign.status);

    setAddingContacts(true);
    try {
      if (shouldCreateGroup) {
        if (!newAudienceGroupName.trim()) {
          toast.warning(t('campaignDetail.messages.groupNameRequired'));
          return;
        }

        const groupRes = await api.post('/api/groups', {
          name: newAudienceGroupName.trim(),
          description: t('campaignDetail.messages.createdAudienceGroupDescription', {
            name: campaign.name,
          }),
          type: 'STATIC',
          ruleConfig: { filters: [] }
        });

        const createdGroupId = groupRes.data?._id;
        await api.post(`/api/groups/${createdGroupId}/members`, {
          workspaceContactIds: selectedIds
        });
        await api.put(`/api/campaigns/${id}`, {
          groupIds: [...new Set([...(campaign.groupIds?.map(g => g._id || g) || []), createdGroupId])]
        });

        toast.success(t('campaignDetail.messages.contactsAddedViaGroup', {
          count: selectedIds.length,
        }));
        await fetchCampaign();
      } else {
        const res = await api.post(`/api/campaigns/${id}/recipients`, {
          workspaceContactIds: selectedIds,
          allowRisky: true,
          allowUnknown: true
        });
        const added = res.data?.results?.added ?? 0;
        const skipped = (res.data?.results?.skipped_duplicate || 0)
          + (res.data?.results?.skipped_suppressed || 0)
          + (res.data?.results?.skipped_invalid || 0)
          + (res.data?.results?.skipped_risky || 0)
          + (res.data?.results?.skipped_unknown || 0)
          + (res.data?.results?.errors?.length || 0);

        toast.success(t('campaignDetail.messages.recipientsUpdated', {
          added,
          skippedText: skipped
            ? t('campaignDetail.messages.skippedText', { count: skipped })
            : '',
        }));
        setRefreshKey(prev => prev + 1);
        fetchMetrics();
      }

      setIsAddContactsOpen(false);
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.addContactsFailed'));
    } finally {
      setAddingContacts(false);
    }
  };

  const openRulesEditor = () => {
    setStartMode(campaign.startMode || 'IMMEDIATE');
    setStartAt(campaign.startAt ? new Date(campaign.startAt).toISOString().slice(0, 16) : '');
    setEndAt(campaign.endAt ? new Date(campaign.endAt).toISOString().slice(0, 16) : '');
    setTimezone(campaign.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
    setWorkingDays(campaign.workingDays || [1, 2, 3, 4, 5]);
    setSendingWindowStart(campaign.sendingWindowStart || '09:00');
    setSendingWindowEnd(campaign.sendingWindowEnd || '17:30');
    setMinDelaySec(campaign.minDelaySec ?? 60);
    setMaxDelaySec(campaign.maxDelaySec ?? 180);
    setPerDayLimit(campaign.perDayLimit ?? 0);
    setDispatchSortPolicy(campaign.dispatchSortPolicy || 'COMPANY_SEQUENTIAL');
    setOpenTracking(campaign.tracking?.openTracking !== false);
    setClickTracking(campaign.tracking?.clickTracking !== false);
    setIsRulesEditorOpen(true);
  };

  const toggleWorkingDay = (dayIndex) => {
    setWorkingDays(prev => (
      prev.includes(dayIndex)
        ? prev.filter(day => day !== dayIndex)
        : [...prev, dayIndex].sort((a, b) => a - b)
    ));
  };

  const handleSaveRules = async () => {
    const parsedMinDelay = parseInt(minDelaySec, 10);
    const parsedMaxDelay = parseInt(maxDelaySec, 10);
    const parsedPerDayLimit = parseInt(perDayLimit, 10);

    if (Number.isNaN(parsedMinDelay) || Number.isNaN(parsedMaxDelay) || parsedMinDelay < 0 || parsedMaxDelay < 0) {
      toast.warning(t('campaignDetail.messages.delayValuesPositive'));
      return;
    }

    if (parsedMinDelay > parsedMaxDelay) {
      toast.warning(t('campaignDetail.messages.minDelayGreater'));
      return;
    }

    if (workingDays.length === 0) {
      toast.warning(t('campaignDetail.messages.selectWorkingDay'));
      return;
    }

    if (startMode === 'SCHEDULED' && !startAt) {
      toast.warning(t('campaignDetail.messages.scheduledRequireStartDate'));
      return;
    }

    setSavingRules(true);
    try {
      await api.put(`/api/campaigns/${id}`, {
        startMode,
        startAt: startMode === 'SCHEDULED' && startAt ? new Date(startAt).toISOString() : undefined,
        endAt: endAt ? new Date(endAt).toISOString() : undefined,
        timezone,
        workingDays,
        sendingWindowStart,
        sendingWindowEnd,
        minDelaySec: parsedMinDelay,
        maxDelaySec: parsedMaxDelay,
        perDayLimit: Number.isNaN(parsedPerDayLimit) ? 0 : parsedPerDayLimit,
        dispatchSortPolicy,
        tracking: {
          openTracking,
          clickTracking
        }
      });
      await fetchCampaign();
      setIsRulesEditorOpen(false);
      toast.success(t('campaignDetail.messages.deliveryRulesUpdated'));
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'campaignDetail.messages.deliveryRulesUpdateFailed'));
    } finally {
      setSavingRules(false);
    }
  };

  const fetchRecipients = useCallback(async () => {
    if (!campaign) return;
    setRecipientsLoading(true);
    try {
      const res = await api.get(`/api/campaigns/${id}/recipients`, {
        params: {
          page: recPage,
          limit: recLimit,
          search: recSearch,
          status: recStatusFilter,
          sortBy: recSortBy,
          sortOrder: recSortOrder
        }
      });
      setRecipients(res.data.data || []);
      setRecTotalRecords(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(t('campaignDetail.messages.loadRecipientsFailed'));
    } finally {
      setRecipientsLoading(false);
    }
  }, [id, campaign, recPage, recLimit, recSearch, recStatusFilter, recSortBy, recSortOrder, t]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients, refreshKey]);

  useEffect(() => {
    if (!isAddContactsOpen) return;
    const timeout = setTimeout(() => {
      fetchAvailableContacts();
    }, 300);

    return () => clearTimeout(timeout);
  }, [fetchAvailableContacts, isAddContactsOpen]);

  const recipientColumns = [
    {
      accessor: 'email',
      header: t('campaignDetail.table.recipient'),
      sortable: true,
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-bold text-sm text-foreground">{r.name || r.email?.split('@')[0] || t('campaignDetail.recipient.unknownRecipient')}</span>
          <span className="text-[10px] font-medium text-muted-foreground">{r.email}</span>
        </div>
      )
    },
    { 
      accessor: 'status', 
      header: t('common.status'),
      sortable: true,
      render: (r) => (
        <div className="flex flex-col gap-1 items-start">
          <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border ${
            r.status === 'sent' ? 'bg-success/10 text-success border-success/20' :
            r.status === 'error' ? 'bg-destructive/10 text-destructive border-destructive/20' :
            r.status === 'processing' ? 'bg-warning/10 text-warning border-warning/20' :
            'bg-muted text-muted-foreground border-border'
          }`}>
            {t(`status.${r.status}`, { defaultValue: r.status })}
          </span>
          {(r.status === 'queued' || r.status === 'processing') && r.waitReason && (
             <span className="text-[10px] text-blue-500 font-bold max-w-[200px] truncate" title={r.waitReason}>
               {r.waitReason}
             </span>
          )}
          {r.status === 'error' && (
            <span className="text-[10px] text-destructive max-w-[200px] truncate" title={r.currentAction || (r.logs && r.logs.length > 0 ? r.logs[r.logs.length - 1] : t('campaignDetail.recipient.unknownError'))}>
              {r.currentAction || (r.logs && r.logs.length > 0 ? r.logs[r.logs.length - 1] : t('campaignDetail.recipient.unknownError'))}
            </span>
          )}
        </div>
      )
    },
    {
      accessor: 'sendAt',
      header: t('campaignDetail.table.trigger'),
      sortable: true,
      render: (r) => (
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold text-foreground">{getCampaignRecipientTriggerLabel(r, t)}</span>
          <span className="text-[10px] font-medium text-muted-foreground">{getCampaignRecipientNextAction(r, t, i18n)}</span>
        </div>
      )
    },
    {
      accessor: 'source',
      header: t('campaignDetail.table.source'),
      sortable: false,
      render: (r) => (
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-primary">{getCampaignRecipientSourceLabel(r, t)}</span>
          <span className="text-[10px] font-medium text-muted-foreground">
            {t(
              r.status === 'sent' ? 'campaignDetail.recipient.sentAt' : 'campaignDetail.recipient.queuedAt',
              {
                date: formatDateTime(getCampaignRecipientTimelineDate(r), {}, i18n.language),
              }
            )}
          </span>
        </div>
      )
    },
    {
      accessor: '_id',
      header: t('campaignDetail.table.actions'),
      sortable: false,
      render: (r) => (
        <div className="flex items-center gap-2">
          {r.status === 'error' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRetryRecipient(r._id);
              }}
              className="rounded-lg bg-primary/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary hover:bg-primary hover:text-white"
            >
              {t('campaignDetail.table.retry')}
            </button>
          )}
          {r.status !== 'sent' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSendRecipientNow(r._id);
              }}
              className="rounded-lg bg-emerald-600/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 hover:bg-emerald-600 hover:text-white"
            >
              {t('campaignDetail.table.sendNow', { defaultValue: 'Hemen Gönder' })}
            </button>
          )}
          {r.status !== 'sent' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveRecipient(r._id);
              }}
              className="rounded-lg bg-destructive/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-destructive hover:bg-destructive hover:text-white"
            >
              {t('campaignDetail.table.remove')}
            </button>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="p-8 max-w-4xl mx-auto flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-16 h-16 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 opacity-50" />
        </div>
        <h2 className="text-xl font-black tracking-tight text-foreground mb-2">
          {error ? t('campaignDetail.page.loadErrorTitle') : t('campaignDetail.page.notFoundTitle')}
        </h2>
        <p className="text-muted-foreground text-sm font-medium mb-6 text-center max-w-md">
          {error || t('campaignDetail.page.notFoundDescription')}
        </p>
        <button
          onClick={() => navigate('/campaigns')}
          className="flex items-center text-[11px] font-black uppercase tracking-widest text-primary hover:bg-primary/10 px-4 py-2 rounded-lg transition-all"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('campaignDetail.page.backToCampaigns')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6 text-foreground bg-background animate-in fade-in duration-500">
      
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate('/campaigns')}
          className="flex items-center w-fit text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-muted/50 px-3 py-1.5 -ml-3 rounded-lg transition-all"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> {t('campaignDetail.page.campaigns')}
        </button>
        
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3">
              {campaign.name}
            </h1>
            <div className="flex items-center gap-3">
              <span className={`px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-lg border ${
                campaign.status === 'DRAFT' ? 'bg-muted text-muted-foreground border-border' :
                campaign.status === 'RUNNING' ? 'bg-success/10 text-success border-success/20' :
                campaign.status === 'PAUSED' ? 'bg-warning/10 text-warning border-warning/20' :
                campaign.status === 'ARCHIVED' ? 'bg-muted/50 text-muted-foreground border-dashed border-border/50' :
                'bg-primary/10 text-primary border-primary/20'
              }`}>
                {campaign.status}
              </span>
              <span className="text-[11px] font-bold text-muted-foreground flex items-center uppercase tracking-widest border-l border-border pl-3">
                <Calendar className="w-3 h-3 mr-1.5" />
                {t('campaignDetail.page.createdOn', {
                  date: formatDate(campaign.createdAt, { dateStyle: 'medium' }, i18n.language),
                })}
              </span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {campaign.status === 'DRAFT' && (
               <button
                 onClick={handleLaunch}
                 disabled={launching || !campaign.templateId || !campaign.groupIds?.length || (!campaign.mailAccountId && !campaign.senderPoolId)}
                 className="bg-primary text-primary-foreground flex items-center px-4 py-2 rounded-xl shadow-lg text-[11px] font-black uppercase tracking-widest hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                 title={(!campaign.templateId || !campaign.groupIds?.length || (!campaign.mailAccountId && !campaign.senderPoolId)) ? t('campaignDetail.page.launchRequirements') : ""}
               >
                 {launching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5 mr-1.5" />} 
                 {launching ? t('campaignDetail.page.launching') : t('campaignDetail.page.launchCampaign')}
               </button>
            )}

            {(campaign.status === 'RUNNING' || campaign.status === 'QUEUED' || campaign.status === 'SCHEDULED') && (
               <button
                 onClick={() => handleStatusChange('PAUSED')}
                 className="bg-warning text-warning-foreground flex items-center px-4 py-2 rounded-xl shadow-lg text-[11px] font-black uppercase tracking-widest hover:opacity-90 active:scale-[0.98] transition-all"
               >
                 <Pause className="w-3.5 h-3.5 mr-1.5 fill-current" /> {t('campaignDetail.page.pause')}
               </button>
            )}
            
            {campaign.status === 'PAUSED' && (
               <button
                 onClick={() => handleStatusChange('RUNNING')}
                 className="bg-success text-success-foreground flex items-center px-4 py-2 rounded-xl shadow-lg text-[11px] font-black uppercase tracking-widest hover:opacity-90 active:scale-[0.98] transition-all"
               >
                 <Play className="w-3.5 h-3.5 mr-1.5 fill-current" /> {t('campaignDetail.page.resume')}
               </button>
            )}

            {campaign.status !== 'ARCHIVED' ? (
               <button
                 onClick={() => handleStatusChange('ARCHIVED')}
                 className="bg-card text-muted-foreground border-2 border-border flex items-center px-4 py-2 rounded-xl shadow-sm hover:text-foreground text-[11px] font-black uppercase tracking-widest hover:bg-muted active:scale-[0.98] transition-all"
               >
                 <Archive className="w-3.5 h-3.5 mr-1.5" /> {t('campaignDetail.page.archive')}
               </button>
            ) : (
               <button
                 onClick={() => handleStatusChange('DRAFT')}
                 className="bg-card text-foreground border-2 border-primary/50 flex items-center px-4 py-2 rounded-xl shadow-sm hover:bg-primary/5 hover:border-primary text-[11px] font-black uppercase tracking-widest active:scale-[0.98] transition-all"
               >
                 <Play className="w-3.5 h-3.5 mr-1.5 text-primary" /> {t('campaignDetail.page.restoreDraft')}
               </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-4">
        
        {/* Left Column: Metrics & Configuration */}
        <div className="lg:col-span-1 space-y-6">
          
          {/* Execution Metrics Panel */}
          {(metrics || launchResult) && (
            <div className="bg-card border-2 border-border rounded-[2rem] p-6 shadow-sm flex flex-col mb-6 animate-in slide-in-from-bottom-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[11px] font-black text-foreground uppercase tracking-widest flex items-center">
                  <BarChart3 className="w-3.5 h-3.5 mr-2 text-primary" /> {t('campaignDetail.sections.executionStatus')}
                </h3>
                {metrics && metrics.error > 0 && (
                  <button
                    onClick={handleRetryAllFailed}
                    className="text-[10px] font-black text-destructive uppercase tracking-widest bg-destructive/10 hover:bg-destructive hover:text-white px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5"
                  >
                    <RefreshCw className="w-3 h-3" /> {t('campaignDetail.sections.retryAllErrors', { defaultValue: 'Hataları Tekrar Dene' })}
                  </button>
                )}
              </div>
              
              {launchResult && (
                <div className={`mb-5 p-3 rounded-2xl border ${launchResult.status === 'success' ? 'bg-success/5 border-success/20 text-success' : 'bg-warning/5 border-warning/20 text-warning'} text-sm`}>
                   <p className="font-bold mb-1">{launchResult.status === 'success' ? t('campaignDetail.sections.expansionComplete') : t('campaignDetail.sections.skippedWarning')}</p>
                   <p className="text-[11px] font-semibold opacity-80">{t('campaignDetail.sections.newlyQueued', { queued: launchResult.totalQueued, skipped: launchResult.totalSkipped })}</p>
                   {launchResult.message && <p className="text-[10px] italic mt-1">{launchResult.message}</p>}
                </div>
              )}

              {metrics ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted/30 border border-border/50 rounded-2xl p-4 flex flex-col text-center shadow-sm">
                    <span className="text-2xl font-black text-foreground">{metrics.total || 0}</span>
                    <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mt-1">{t('campaignDetail.sections.totalAudience')}</span>
                  </div>
                  <div className="bg-success/5 border border-success/20 rounded-2xl p-4 flex flex-col text-center shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-1.5 bg-success/20 rounded-bl-lg">
                      <span className="text-[8px] font-black uppercase text-success tracking-widest">{t('campaignDetail.sections.today')}</span>
                    </div>
                    <span className="text-2xl font-black text-success">{campaign.stats?.sentToday || 0}</span>
                    <span className="text-[10px] font-black text-success uppercase tracking-widest mt-1">{t('campaignDetail.sections.sentToday')}</span>
                  </div>
                  <div className="bg-warning/10 border border-warning/20 rounded-2xl p-4 flex flex-col text-center shadow-sm">
                    <span className="text-2xl font-black text-warning">{metrics.queued || 0}</span>
                    <span className="text-[10px] font-black text-warning uppercase tracking-widest mt-1">{t('campaignDetail.sections.queued')}</span>
                  </div>
                  <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 flex flex-col text-center shadow-sm">
                      <span className="text-2xl font-black text-primary">{campaign.stats?.sent || metrics.sent || 0}</span>
                      <span className="text-[10px] font-black text-primary uppercase tracking-widest mt-1">{t('campaignDetail.sections.totalSent')}</span>
                   </div>
                   <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4 flex flex-col text-center shadow-sm">
                      <span className="text-2xl font-black text-emerald-400">{metrics.estimatedDailyCapacity ?? 0}</span>
                      <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mt-1">{t('campaignDetail.sections.estimatedPerDay')}</span>
                      <span className="text-[10px] font-semibold text-emerald-400/70 mt-1">
                        {t('campaignDetail.sections.remainingToSend', { defaultValue: 'Remaining to send' })}: {metrics.remaining ?? 0}
                      </span>
                      <span className="text-[10px] font-semibold text-emerald-400/70">
                        {t('campaignDetail.sections.activeSenders', { defaultValue: 'Active senders' })}: {metrics.activeSenderCount ?? 0}
                      </span>
                   </div>
                   <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-2xl p-4 flex flex-col text-center shadow-sm">
                      <span className="text-2xl font-black text-cyan-400">{metrics.estimatedDaysToFinish ?? '-'}</span>
                      <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest mt-1">{t('campaignDetail.sections.estimatedDaysToFinish')}</span>
                      <span className="text-[10px] font-semibold text-cyan-400/70 mt-1">
                        {metrics.estimatedCompletionAt
                          ? `${t('campaignDetail.sections.estimatedCompletion')}: ${formatDateTime(metrics.estimatedCompletionAt, {}, i18n.language)}`
                          : '-'}
                      </span>
                   </div>
                   <div className="bg-orange-500/10 border border-orange-500/20 rounded-2xl p-4 flex flex-col text-center shadow-sm relative group cursor-help" title={`Unique: ${campaign.stats?.uniqueOpened || 0} | Total: ${campaign.stats?.opened || 0}`}>
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-2xl font-black text-orange-400">{campaign.stats?.uniqueOpened || 0}</span>
                        <span className="text-[10px] font-bold text-orange-400/70">({campaign.stats?.sent > 0 ? Math.round((campaign.stats.uniqueOpened / campaign.stats.sent) * 100) : 0}%)</span>
                      </div>
                      <span className="text-[10px] font-black text-orange-400 uppercase tracking-widest mt-1">{t('campaignDetail.sections.uniqueOpens')}</span>
                   </div>
                   <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl p-4 flex flex-col text-center shadow-sm relative group cursor-help" title={`Unique: ${campaign.stats?.uniqueClicked || 0} | Total: ${campaign.stats?.clicked || 0}`}>
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-2xl font-black text-purple-400">{campaign.stats?.uniqueClicked || 0}</span>
                        <span className="text-[10px] font-bold text-purple-400/70">({campaign.stats?.sent > 0 ? Math.round((campaign.stats.uniqueClicked / campaign.stats.sent) * 100) : 0}%)</span>
                      </div>
                      <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest mt-1">{t('campaignDetail.sections.uniqueClicks')}</span>
                   </div>
                   <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 flex flex-col text-center shadow-sm">
                      <span className="text-2xl font-black text-blue-400">{campaign.stats?.replied || 0}</span>
                      <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest mt-1">{t('campaignDetail.sections.replies')}</span>
                   </div>
                   <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 flex flex-col text-center shadow-sm">
                      <span className="text-2xl font-black text-red-400">{campaign.stats?.bounced || 0}</span>
                      <span className="text-[10px] font-black text-red-400 uppercase tracking-widest mt-1">{t('campaignDetail.sections.bounces')}</span>
                   </div>
                </div>
              ) : (
                <div className="text-center p-4">
                   <Loader2 className="w-5 h-5 text-muted-foreground animate-spin mx-auto mb-2" />
                   <p className="text-xs text-muted-foreground font-medium">{t('campaignDetail.sections.loadingMetrics')}</p>
                </div>
              )}
            </div>
          )}

          {/* Configuration Panel */}
          <div className="bg-card border-2 border-border rounded-[2rem] p-6 shadow-sm flex flex-col">
            <h3 className="text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-4 flex items-center">
              <Settings className="w-3.5 h-3.5 mr-2" /> {t('campaignDetail.sections.configuration')}
            </h3>
            
            <div className="space-y-5">
              {/* Sending Identity */}
              <div>
                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1 opacity-70">{t('campaignDetail.sections.sendingIdentity')}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={openSenderEditor}
                    className="inline-flex items-center px-3 py-2 rounded-xl border border-border bg-background hover:bg-muted text-[10px] font-black uppercase tracking-widest transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                    {t('campaignDetail.sections.editSender')}
                  </button>
                </div>
                {campaign.mailAccountId ? (
                  <div className="flex items-start gap-3 bg-muted/30 p-3 rounded-2xl border-2 border-border/50 mb-4">
                     <div className="bg-primary/10 p-2 rounded-xl text-primary"><Rocket className="w-5 h-5" /></div>
                     <div className="flex-1 truncate">
                       <p className="text-xs font-black uppercase tracking-widest text-muted-foreground/60 mb-0.5">{t('campaignDetail.sections.explicitAccount')}</p>
                       <p className="text-sm font-bold text-foreground truncate">{campaign.mailAccountId.fromName || campaign.mailAccountId.user}</p>
                       <p className="text-[11px] font-semibold text-muted-foreground truncate">{campaign.mailAccountId.user}</p>
                     </div>
                  </div>
                ) : campaign.senderPoolId ? (
                  <div className="flex items-start gap-3 bg-muted/30 p-3 rounded-2xl border-2 border-border/50 mb-4 hover:border-primary/30 transition-colors cursor-pointer" onClick={() => navigate('/sender-pools')}>
                     <div className="bg-indigo-500/10 p-2 rounded-xl text-indigo-500"><Layers className="w-5 h-5" /></div>
                     <div className="flex-1 truncate">
                       <p className="text-xs font-black uppercase tracking-widest text-muted-foreground/60 mb-0.5">{t('campaignDetail.sections.senderPool')}</p>
                       <div className="flex items-center gap-2">
                         <p className="text-sm font-bold text-foreground truncate">{campaign.senderPoolId.name}</p>
                         <span className="text-[9px] font-black uppercase tracking-tighter bg-indigo-500/10 text-indigo-500 px-1 border border-indigo-500/20 rounded">
                           {campaign.senderPoolId.rotationStrategy?.replace(/_/g, ' ')}
                         </span>
                       </div>
                       <p className="text-[11px] font-semibold text-muted-foreground truncate">{t('campaignDetail.sections.members', { count: campaign.senderPoolId.mailAccountIds?.length || 0 })}</p>
                     </div>
                  </div>
                ) : (
                  <div className="text-sm font-black text-warning italic border border-dashed border-warning/50 rounded-xl p-3 text-center mb-4 text-[11px]">{t('campaignDetail.sections.missingSender')}</div>
                )}
              </div>

              {/* Creative Asset */}
              <div>
                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1 opacity-70">{t('campaignDetail.sections.creativeAsset')}</p>
                {campaign.templateId ? (
                  <div className="flex items-start gap-3 bg-muted/30 p-3 rounded-2xl border-2 border-border/50">
                     <div className="bg-primary/10 p-2 rounded-xl text-primary"><FileSignature className="w-5 h-5" /></div>
                     <div className="flex-1 truncate">
                       <p className="text-sm font-bold text-foreground truncate">{campaign.templateId.name}</p>
                       <p className="text-[11px] font-semibold text-muted-foreground truncate">{campaign.templateId.subject}</p>
                     </div>
                  </div>
                ) : (
                  <div className="text-sm font-medium text-muted-foreground italic border border-dashed border-border rounded-xl p-3 text-center">{t('campaignDetail.sections.noTemplateAttached')}</div>
                )}
              </div>

              {/* Target Audience */}
              <div>
                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1 opacity-70 mt-4">{t('campaignDetail.sections.targetAudience')}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={openAudienceEditor}
                    className="inline-flex items-center px-3 py-2 rounded-xl border border-border bg-background hover:bg-muted text-[10px] font-black uppercase tracking-widest transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                    {t('campaignDetail.sections.manageGroups')}
                  </button>
                  {campaign.groupIds && campaign.groupIds.length > 0 && (
                    <button
                      onClick={handleSyncAudience}
                      disabled={syncingAudience}
                      className="inline-flex items-center px-3 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50"
                    >
                      {syncingAudience ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                      {t('campaignDetail.sections.syncAudience', { defaultValue: 'Sync Audience' })}
                    </button>
                  )}
                  {campaign.status !== 'DRAFT' && campaign.status !== 'ARCHIVED' && (
                    <button
                      onClick={handleSyncQueues}
                      disabled={syncingQueues}
                      className="inline-flex items-center px-3 py-2 rounded-xl border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50"
                    >
                      {syncingQueues ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                      {t('campaignDetail.sections.syncQueues', { defaultValue: 'Sync Queues' })}
                    </button>
                  )}
                  <button
                    onClick={openAddContactsModal}
                    className="inline-flex items-center px-3 py-2 rounded-xl border border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 text-[10px] font-black uppercase tracking-widest transition-colors"
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                    {['DRAFT', 'ARCHIVED', 'SCHEDULED'].includes(campaign.status) ? t('campaignDetail.sections.addContactsAsGroup') : t('campaignDetail.sections.addContacts')}
                  </button>
                </div>
                {campaign.groupIds && campaign.groupIds.length > 0 ? (
                  <div className="space-y-2">
                    {campaign.groupIds.map(g => (
                      <div key={g._id} className="flex items-center gap-3 bg-muted/30 p-3 rounded-2xl border-2 border-border/50">
                        <div className="bg-primary/10 p-2 rounded-xl text-primary"><Users className="w-4 h-4" /></div>
                        <div className="flex-1 truncate">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-foreground truncate">{g.name}</p>
                            <span className={`text-[8px] font-black uppercase tracking-tighter px-1.5 py-0.5 rounded border ${g.type === 'DYNAMIC' ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20' : 'bg-muted text-muted-foreground border-border'}`}>
                              {g.type || 'STATIC'}
                            </span>
                          </div>
                          <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground truncate mt-0.5">{g.description || t('campaignDetail.sections.noDescription')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm font-medium text-muted-foreground italic border border-dashed border-border rounded-xl p-3 text-center">{t('campaignDetail.sections.noGroupsAttached')}</div>
                )}
              </div>

              {/* Delivery Rules */}
              <div>
                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1 opacity-70 mt-4">{t('campaignDetail.sections.deliveryRules')}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={openRulesEditor}
                    className="inline-flex items-center px-3 py-2 rounded-xl border border-border bg-background hover:bg-muted text-[10px] font-black uppercase tracking-widest transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                    {t('campaignDetail.sections.editDeliveryRules')}
                  </button>
                </div>
                <div className="bg-muted/30 p-4 rounded-2xl border-2 border-border/50 space-y-3">
                   <div className="flex justify-between items-center bg-card rounded-lg p-2 border border-border/50">
                      <span className="text-xs font-bold text-muted-foreground">{t('campaignDetail.sections.mode')}</span>
                      <span className="text-xs font-black uppercase text-foreground">{campaign.startMode || 'IMMEDIATE'}</span>
                   </div>
                   {campaign.startMode === 'SCHEDULED' && campaign.startAt && (
                     <div className="flex justify-between items-center bg-card rounded-lg p-2 border border-border/50">
                        <span className="text-xs font-bold text-muted-foreground">{t('campaignDetail.sections.scheduledStart')}</span>
                        <span className="text-xs font-bold text-primary">{formatDateTime(campaign.startAt, {}, i18n.language)}</span>
                     </div>
                   )}
                   <div className="flex justify-between items-center bg-card rounded-lg p-2 border border-border/50">
                      <span className="text-xs font-bold text-muted-foreground">{t('campaignDetail.sections.pacing')}</span>
                      <span className="text-xs font-bold text-foreground">{campaign.minDelaySec || 0}s to {campaign.maxDelaySec || 0}s</span>
                   </div>
                   <div className="flex justify-between items-center bg-card rounded-lg p-2 border border-border/50">
                      <span className="text-xs font-bold text-muted-foreground">{t('campaignDetail.sections.dailyLimit')}</span>
                      <span className="text-xs font-black text-warning">{campaign.perDayLimit > 0 ? campaign.perDayLimit : t('common.unlimited')}</span>
                   </div>
                   <div className="flex flex-col gap-1 bg-card rounded-lg p-2 border border-border/50">
                      <div className="flex justify-between items-center">
                         <span className="text-xs font-bold text-muted-foreground">{t('campaignDetail.sections.window', { timezone: campaign.timezone || 'UTC' })}</span>
                         <span className="text-xs font-bold text-foreground">
                            {campaign.sendingWindowStart || '00:00'} - {campaign.sendingWindowEnd || '23:59'}
                         </span>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1 text-right">
                         {(campaign.workingDays || [])
                           .map((d) => [
                             t('campaignDetail.days.sun'),
                             t('campaignDetail.days.mon'),
                             t('campaignDetail.days.tue'),
                             t('campaignDetail.days.wed'),
                             t('campaignDetail.days.thu'),
                             t('campaignDetail.days.fri'),
                             t('campaignDetail.days.sat'),
                           ][d])
                           .join(', ')}
                      </div>
                      <div className="flex justify-between items-center bg-card rounded-lg p-2 border border-border/50 mt-2">
                         <span className="text-xs font-bold text-muted-foreground">Open Tracking</span>
                         <span className="text-xs font-black uppercase text-foreground">{campaign.tracking?.openTracking !== false ? 'ON' : 'OFF'}</span>
                      </div>
                      <div className="flex justify-between items-center bg-card rounded-lg p-2 border border-border/50">
                         <span className="text-xs font-bold text-muted-foreground">Click Tracking</span>
                         <span className="text-xs font-black uppercase text-foreground">{campaign.tracking?.clickTracking !== false ? 'ON' : 'OFF'}</span>
                      </div>
                   </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Template Preview */}
        <div className="lg:col-span-2 space-y-6">
           <div className="bg-card border-2 border-border rounded-[2rem] overflow-hidden shadow-sm flex flex-col h-full min-h-[600px]">
             <div className="px-6 py-4 border-b-2 border-border bg-muted/10 flex items-center justify-between">
                <h3 className="text-[11px] font-black text-muted-foreground uppercase tracking-widest flex items-center">
                  <FileSignature className="w-3.5 h-3.5 mr-2" /> {t('campaignDetail.sections.contentPreview')}
                </h3>
             </div>
             
             <div className="p-6 flex-1 bg-background relative">
                {campaign.templateId && campaign.templateId.bodyHtml ? (
                  <div className="w-full h-full max-w-[600px] mx-auto bg-white text-black p-8 rounded-xl border border-gray-200 shadow-sm overflow-auto" 
                       dangerouslySetInnerHTML={{ __html: campaign.templateId.bodyHtml }} />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                     <FileSignature className="w-12 h-12 opacity-20 mb-3" />
                     <p className="text-sm font-medium">{t('campaignDetail.sections.noHtmlPreview')}</p>
                  </div>
                )}
             </div>
           </div>

           {/* Engagement Timeline (if detailed analytics available) */}
           {detailedAnalytics?.engagementTimeline?.length > 0 && (
             <div className="bg-card border-2 border-border rounded-[2rem] p-6 shadow-sm flex flex-col">
               <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-4 flex items-center">
                 <BarChart3 className="w-3 h-3 mr-1.5" /> {t('campaignDetail.sections.engagementTimeline')}
               </h4>
               <div className="h-48 w-full flex items-end gap-1 px-2">
                 {detailedAnalytics.engagementTimeline.map((day, idx) => {
                   const total = day.events.reduce((acc, e) => acc + e.count, 0);
                   const maxTotal = Math.max(...detailedAnalytics.engagementTimeline.map(d => d.events.reduce((acc, e) => acc + e.count, 0)), 1);
                   const height = Math.max((total / maxTotal) * 100, 5);
                   return (
                     <div key={idx} className="flex-1 group relative flex flex-col items-center justify-end h-full">
                       <div className="absolute bottom-full mb-2 hidden group-hover:block bg-foreground text-background text-[10px] font-bold px-2 py-1 rounded shadow-lg z-10 whitespace-nowrap">
                         {day._id}: {total} {t('campaignDetail.sections.events')}
                       </div>
                       <div 
                         className="w-full bg-primary/20 hover:bg-primary/40 rounded-t-sm transition-all duration-300" 
                         style={{ height: `${height}%` }}
                       />
                       <span className="text-[8px] font-black text-muted-foreground uppercase tracking-tighter mt-1 opacity-50 truncate w-full text-center">
                         {day._id.split('-').slice(1).join('/')}
                       </span>
                     </div>
                   );
                 })}
               </div>
             </div>
           )}
        </div>

      </div>

      {/* Recipients Table */}
      <div className="pt-4 lg:col-span-3">
          <div className="bg-card border-2 border-border rounded-[2rem] p-6 shadow-sm flex flex-col mb-6">
            <h3 className="text-[11px] font-black text-foreground uppercase tracking-widest mb-4 flex items-center">
              <Users className="w-3.5 h-3.5 mr-2 text-primary" /> {t('campaignDetail.sections.campaignRecipients')}
            </h3>
            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">{t('campaignDetail.sections.statusHelpTitle')}</p>
                <p className="text-[11px] font-medium text-foreground">{t('campaignDetail.sections.statusHelpDescription')}</p>
              </div>
              <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">{t('campaignDetail.sections.triggerHelpTitle')}</p>
                <p className="text-[11px] font-medium text-foreground">{t('campaignDetail.sections.triggerHelpDescription')}</p>
              </div>
              <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">{t('campaignDetail.sections.sourceHelpTitle')}</p>
                <p className="text-[11px] font-medium text-foreground">{t('campaignDetail.sections.sourceHelpDescription')}</p>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border-2 border-border/50">
              <DataTable 
                columns={recipientColumns}
                data={recipients}
                loading={recipientsLoading}
                pagination={{
                  page: recPage,
                  limit: recLimit,
                  totalRecords: recTotalRecords,
                  totalPages: Math.ceil(recTotalRecords / recLimit) || 1
                }}
                onPageChange={setRecPage}
                onLimitChange={setRecLimit}
                sort={{
                  sortBy: recSortBy,
                  sortOrder: recSortOrder
                }}
                onSort={(field) => {
                   if (recSortBy === field) {
                       setRecSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                   } else {
                       setRecSortBy(field);
                       setRecSortOrder('desc');
                   }
                   setRecPage(1);
                }}
                search={{
                  placeholder: t('campaignDetail.table.searchPlaceholder'),
                  value: recSearch,
                  onChange: val => { setRecSearch(val); setRecPage(1); }
                }}
                filter={{
                  value: recStatusFilter,
                  onChange: val => { setRecStatusFilter(val); setRecPage(1); },
                  options: [
                    { label: t('campaignDetail.table.allStatuses'), value: 'All' },
                    { label: t('status.queued'), value: 'queued' },
                    { label: t('status.processing'), value: 'processing' },
                    { label: t('status.sent'), value: 'sent' },
                    { label: t('campaignDetail.table.error'), value: 'error' }
                  ]
                }}
                emptyState={
                  <div className="text-center py-10 bg-card rounded-[2rem] border-2 border-dashed border-border shadow-sm flex flex-col items-center justify-center bg-muted/5">
                     <p className="text-sm font-medium text-muted-foreground">{t('campaignDetail.table.empty')}</p>
                  </div>
                }
              />
            </div>
          </div>
      </div>

      {/* Launch Confirmation Dialog */}
      <AlertDialog open={isLaunchConfirmOpen} onOpenChange={setIsLaunchConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('campaignDetail.dialogs.launchTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('campaignDetail.dialogs.launchDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLaunch} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {t('campaignDetail.dialogs.launchNow')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isAudienceEditorOpen} onOpenChange={setIsAudienceEditorOpen}>
        <DialogContent className="max-w-3xl rounded-[2rem] border-2 border-border p-0 overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-border bg-muted/10">
            <DialogTitle className="text-xl font-black tracking-tight">{t('campaignDetail.dialogs.manageCampaignGroups')}</DialogTitle>
            <DialogDescription>
              {t('campaignDetail.dialogs.attachGroupsDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="p-6">
            <div className="max-h-[55vh] overflow-y-auto border-2 border-border/50 rounded-2xl bg-muted/10 p-3 space-y-2">
              {groupsLoading ? (
                <div className="py-12 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : availableGroups.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  {t('campaignDetail.dialogs.noGroupsFound')}
                </div>
              ) : (
                availableGroups.map(group => {
                  const isSelected = selectedGroupIds.includes(group._id);
                  return (
                    <button
                      key={group._id}
                      type="button"
                      onClick={() => toggleSelectedGroup(group._id)}
                      className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${isSelected ? 'bg-primary/5 border-primary shadow-sm' : 'bg-background border-border/50 hover:border-primary/40'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30 bg-background'}`}>
                          {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-bold truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>{group.name}</p>
                            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">
                              {t('campaignDetail.dialogs.contactsCount', { count: group.memberCount || 0 })}
                            </span>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1 truncate">
                            {group.description || t('campaignDetail.sections.noDescription')}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-5 border-t border-border bg-muted/10">
            <Button variant="ghost" onClick={() => setIsAudienceEditorOpen(false)} className="h-11 rounded-xl border border-border">
              {t('actions.cancel')}
            </Button>
            <Button onClick={handleSaveAudience} disabled={savingAudience} className="h-11 rounded-xl font-bold">
              {savingAudience ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('campaignDetail.dialogs.saveAudience')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddContactsOpen} onOpenChange={setIsAddContactsOpen}>
        <DialogContent className="max-w-3xl rounded-[2rem] border-2 border-border p-0 overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-border bg-muted/10">
            <DialogTitle className="text-xl font-black tracking-tight">
              {['DRAFT', 'ARCHIVED', 'SCHEDULED'].includes(campaign.status) ? t('campaignDetail.dialogs.addContactsAsStaticGroup') : t('campaignDetail.dialogs.addDirectRecipients')}
            </DialogTitle>
            <DialogDescription>
              {['DRAFT', 'ARCHIVED', 'SCHEDULED'].includes(campaign.status)
                ? t('campaignDetail.dialogs.addStaticGroupDescription')
                : t('campaignDetail.dialogs.addDirectDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4">
            {['DRAFT', 'ARCHIVED', 'SCHEDULED'].includes(campaign.status) && (
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2">
                  {t('campaignDetail.dialogs.groupName')}
                </label>
                <input
                  type="text"
                  value={newAudienceGroupName}
                  onChange={(e) => setNewAudienceGroupName(e.target.value)}
                  placeholder={t('campaignDetail.dialogs.groupNamePlaceholder')}
                  className="w-full h-11 px-4 rounded-2xl border-2 border-border/50 bg-muted/20 focus:border-primary outline-none font-semibold"
                />
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                autoFocus
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder={t('campaignDetail.dialogs.searchContactsPlaceholder')}
                className="w-full h-11 pl-11 pr-4 rounded-2xl border-2 border-border/50 bg-muted/20 focus:border-primary outline-none font-semibold"
              />
            </div>

            <div className="max-h-[45vh] overflow-y-auto border-2 border-border/50 rounded-2xl bg-muted/10 p-3 space-y-2">
              {contactsLoading ? (
                <div className="py-12 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : availableContacts.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  {t('campaignDetail.dialogs.noContactsFound')}
                </div>
              ) : (
                availableContacts.map(contact => {
                  const contactId = contact.id || contact._id;
                  const isSelected = selectedContactIds.has(contactId);
                  return (
                    <button
                      key={contactId}
                      type="button"
                      onClick={() => toggleContact(contactId)}
                      className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${isSelected ? 'bg-primary/5 border-primary shadow-sm' : 'bg-background border-border/50 hover:border-primary/40'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30 bg-background'}`}>
                          {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-bold truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                            {contact.displayName || contact.name || t('campaignDetail.dialogs.unnamedContact')}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {contact.displayEmail || contact.email || 'No email'}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-5 border-t border-border bg-muted/10">
            <Button variant="ghost" onClick={() => setIsAddContactsOpen(false)} className="h-11 rounded-xl border border-border">
              {t('actions.cancel')}
            </Button>
            <Button onClick={handleAddContacts} disabled={addingContacts || selectedContactIds.size === 0} className="h-11 rounded-xl font-bold">
              {addingContacts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              {selectedContactIds.size > 0
                ? t('campaignDetail.dialogs.addContactsButton', { count: selectedContactIds.size })
                : t('campaignDetail.sections.addContacts')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRulesEditorOpen} onOpenChange={setIsRulesEditorOpen}>
        <DialogContent className="max-w-3xl rounded-[2rem] border-2 border-border p-0 overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-border bg-muted/10">
            <DialogTitle className="text-xl font-black tracking-tight">{t('campaignDetail.dialogs.editDeliveryRulesTitle')}</DialogTitle>
            <DialogDescription>
              {t('campaignDetail.dialogs.editDeliveryRulesDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setStartMode('IMMEDIATE')}
                className={`cursor-pointer rounded-2xl p-4 border-2 text-left transition-all ${startMode === 'IMMEDIATE' ? 'border-primary bg-primary/5 text-primary shadow-sm' : 'border-border/50 bg-background text-muted-foreground hover:border-primary/50'}`}
              >
                <h4 className="font-black tracking-tight mb-1">{t('campaignDetail.dialogs.startNow')}</h4>
                <p className="text-[10px] leading-tight font-medium opacity-80">{t('campaignDetail.dialogs.startNowDescription')}</p>
              </button>
              <button
                type="button"
                onClick={() => setStartMode('SCHEDULED')}
                className={`cursor-pointer rounded-2xl p-4 border-2 text-left transition-all ${startMode === 'SCHEDULED' ? 'border-primary bg-primary/5 text-primary shadow-sm' : 'border-border/50 bg-background text-muted-foreground hover:border-primary/50'}`}
              >
                <h4 className="font-black tracking-tight mb-1">{t('campaignDetail.dialogs.schedule')}</h4>
                <p className="text-[10px] leading-tight font-medium opacity-80">{t('campaignDetail.dialogs.scheduleDescription')}</p>
              </button>
            </div>

            {startMode === 'SCHEDULED' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.startAt')}</label>
                  <input
                    type="datetime-local"
                    value={startAt}
                    onChange={e => setStartAt(e.target.value)}
                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.endAtOptional')}</label>
                  <input
                    type="datetime-local"
                    value={endAt}
                    onChange={e => setEndAt(e.target.value)}
                    className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                  />
                </div>
              </div>
            )}

            {startMode !== 'SCHEDULED' && (
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.endAtOptional')}</label>
                <input
                  type="datetime-local"
                  value={endAt}
                  onChange={e => setEndAt(e.target.value)}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.sendingTimezone')}</label>
              <TimezoneSelector value={timezone} onChange={setTimezone} />
            </div>

            <div>
              <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.workingDays')}</label>
              <div className="flex flex-wrap gap-2">
                {[
                  t('campaignDetail.days.sun'),
                  t('campaignDetail.days.mon'),
                  t('campaignDetail.days.tue'),
                  t('campaignDetail.days.wed'),
                  t('campaignDetail.days.thu'),
                  t('campaignDetail.days.fri'),
                  t('campaignDetail.days.sat'),
                ].map((day, idx) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleWorkingDay(idx)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${workingDays.includes(idx) ? 'bg-primary text-white shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.windowStart')}</label>
                <input
                  type="time"
                  value={sendingWindowStart}
                  onChange={e => setSendingWindowStart(e.target.value)}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.windowEnd')}</label>
                <input
                  type="time"
                  value={sendingWindowEnd}
                  onChange={e => setSendingWindowEnd(e.target.value)}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.minDelay')}</label>
                <input
                  type="number"
                  value={minDelaySec}
                  onChange={e => setMinDelaySec(e.target.value)}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.maxDelay')}</label>
                <input
                  type="number"
                  value={maxDelaySec}
                  onChange={e => setMaxDelaySec(e.target.value)}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">{t('campaignDetail.dialogs.dailyLimit')}</label>
                <input
                  type="number"
                  value={perDayLimit}
                  onChange={e => setPerDayLimit(e.target.value)}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                />
              </div>
              <div className="col-span-full">
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Gönderim Sıralaması / Dispatch Order</label>
                <select
                  value={dispatchSortPolicy}
                  onChange={e => setDispatchSortPolicy(e.target.value)}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                >
                  <option value="COMPANY_SEQUENTIAL">🏢 Şirket Bazlı Sıralı (Bir Şirket Bitince Diğerine)</option>
                  <option value="COMPANY_INTERLEAVED">🔄 Şirketler Arası Dönüşümlü (Spam Korumalı)</option>
                  <option value="SENIORITY_FIRST">👑 Kıdem / Unvan Öncelikli (CEO/VP İlk Sırada)</option>
                  <option value="DEFAULT">⏱️ Eklenme Sırasına Göre</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-6 pt-2">
              <div className="flex items-center gap-3">
                 <button
                   type="button"
                   onClick={() => setOpenTracking(!openTracking)}
                   className={`w-12 h-6 rounded-full transition-all relative border-2 ${openTracking ? 'bg-primary border-primary' : 'bg-muted border-border/50'}`}
                 >
                   <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${openTracking ? 'left-6' : 'left-1'}`} />
                 </button>
                 <span className="text-xs font-bold text-foreground">Open Tracking</span>
              </div>
              <div className="flex items-center gap-3">
                 <button
                   type="button"
                   onClick={() => setClickTracking(!clickTracking)}
                   className={`w-12 h-6 rounded-full transition-all relative border-2 ${clickTracking ? 'bg-primary border-primary' : 'bg-muted border-border/50'}`}
                 >
                   <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${clickTracking ? 'left-6' : 'left-1'}`} />
                 </button>
                 <span className="text-xs font-bold text-foreground">Click Tracking</span>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-5 border-t border-border bg-muted/10">
            <Button variant="ghost" onClick={() => setIsRulesEditorOpen(false)} className="h-11 rounded-xl border border-border">
              {t('actions.cancel')}
            </Button>
            <Button onClick={handleSaveRules} disabled={savingRules} className="h-11 rounded-xl font-bold">
              {savingRules ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('campaignDetail.dialogs.saveRules')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
